import {Word, Alignment, CaptionLayout} from '@contentlab/types/captions';

// Common interfaces
export interface WordBox {
  x: number;
  y: number;
  width: number;
}

export interface WordInfo {
  word: Word;
  index: number;
}

// Word position calculations used by both animations
export function getPrePrintDelta(width: number, alignment: Alignment): number {
  switch (alignment) {
    case Alignment.CENTER:
      return width / 2;
    case Alignment.LEFT:
      return 0;
    case Alignment.RIGHT:
      return width;
    default:
      return width / 2;
  }
}

export function getPostPrintDelta(width: number, alignment: Alignment): number {
  switch (alignment) {
    case Alignment.CENTER:
      return width / 2;
    case Alignment.LEFT:
      return width;
    case Alignment.RIGHT:
      return 0;
    default:
      return width / 2;
  }
}

export function calculateWordPrintOffsets(
  wordWidth: number,
  alignment: Alignment
): {preDelta: number; postDelta: number} {
  return {
    preDelta: getPrePrintDelta(wordWidth, alignment),
    postDelta: getPostPrintDelta(wordWidth, alignment),
  };
}

// Word finding function used by both animations
export function findCurrentAndPreviousWords(
  wordTimings: Word[],
  currentTime: number,
  isCurrentWord: (word: Word, currentTime: number) => boolean
): {current: WordInfo | null; previous: WordInfo | null} {
  for (let i = 0; i < wordTimings.length; i += 1) {
    const word = wordTimings[i];
    if (isCurrentWord(word, currentTime)) {
      return {
        current: {word, index: i},
        previous: i > 0 ? {word: wordTimings[i - 1], index: i - 1} : null,
      };
    }
  }
  return {current: null, previous: null};
}

// Word box calculations used by both animations
export function calculateWordBoxes(
  lines: string[],
  wordTimings: Word[],
  position: {x: number; lineHeight: number; startY: number},
  context: CanvasRenderingContext2D,
  textAlignment: Alignment,
  getFirstWordPosition: (
    x: number,
    lineWidth: number,
    alignment: Alignment
  ) => number,
  calculateOffsets: (
    wordWidth: number,
    alignment: Alignment
  ) => {preDelta: number; postDelta: number},
  captionLayout: CaptionLayout
): WordBox[] {
  const wordBoxes: WordBox[] = [];
  let globalWordIndex = 0;

  lines.forEach((line, lineIndex) => {
    // uses cached word order and word widths
    const {wordCount, wordOrder, wordWidths} =
      captionLayout.lineLayouts[lineIndex];
    const y = position.startY + lineIndex * position.lineHeight;
    const lineWidth = context.measureText(line).width;
    let x = getFirstWordPosition(position.x, lineWidth, textAlignment);

    for (let i = 0; i < wordCount; i += 1) {
      const localWordIndex = wordOrder[i];
      const word = wordTimings[globalWordIndex + localWordIndex];
      if (!word) break;

      const wordWidth = wordWidths[localWordIndex];
      const {preDelta, postDelta} = calculateOffsets(wordWidth, textAlignment);
      x += preDelta;

      wordBoxes[globalWordIndex + localWordIndex] = {x, y, width: wordWidth};

      x += postDelta + context.measureText(' ').width;
    }
    globalWordIndex += wordCount;
  });

  return wordBoxes;
}
